import React, { useEffect, useState } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * Truncated text that keeps the full value one hover — or one click — away.
 *
 * A reference or a tracking number is long enough to widen its whole column,
 * but it is also exactly the kind of value people copy to paste somewhere else.
 * So it is cut to fit, kept whole in the tooltip, and copyable without having
 * to select it by hand.
 *
 * The copy button holds its space even while invisible: revealing it on hover
 * would otherwise reflow the row under the cursor.
 *
 * @param {object} props
 * @param {string|null} props.value - The full value; when empty, `emptyLabel` shows instead.
 * @param {string} [props.className] - Typography of the text itself.
 * @param {string} [props.maxWidth='120px'] - Where the text starts being cut, or wraps when `wrap` is on.
 * @param {boolean} [props.wrap=false] - Show the whole value, breaking it onto new lines, instead of cutting it.
 * @param {string} [props.emptyLabel='-']
 * @param {string} [props.emptyClassName] - Typography when there is no value.
 */
export const CopyableText = ({
  value,
  className = '',
  maxWidth = '120px',
  wrap = false,
  emptyLabel = '-',
  emptyClassName = '',
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!value) {
    return <span className={emptyClassName || className}>{emptyLabel}</span>;
  }

  const handleCopy = async (event) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // The clipboard is unavailable outside a secure context. The tooltip
      // still carries the full value, so there is nothing worth reporting.
    }
  };

  return (
    <span className="group/copy flex items-center gap-1 min-w-0">
      <span
        title={value}
        className={`${wrap ? 'whitespace-normal break-all' : 'truncate'} ${className}`}
        style={{ maxWidth }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copiado' : `Copiar ${value}`}
        title={copied ? 'Copiado' : 'Copiar'}
        className="shrink-0 opacity-0 group-hover/copy:opacity-100 focus:opacity-100 text-text-muted hover:text-ninja-green transition-opacity"
      >
        {copied ? <Check size={12} className="text-ninja-green" /> : <Copy size={12} />}
      </button>
    </span>
  );
};
